import { apiClient } from '@rti/shared';
import {
  DriversCommentary,
  PECommentary,
  PPCommentary,
  PSCommentary,
  PVCCommentary,
  ForecastCommentary,
} from '@rti/shared/src/types';

// ==================== Drivers Commentary API ====================

export const driversAPI = {
  getAll: async (): Promise<DriversCommentary[]> => {
    return apiClient.getDriversCommentary();
  },

  getCount: async (): Promise<number> => {
    return apiClient.getDriversCommentaryCount();
  },

  getLatestUpdateTime: async (): Promise<{ latest_update_time: string }> => {
    return apiClient.getDriversCommentaryLatest();
  },

  create: async (data: DriversCommentary): Promise<DriversCommentary> => {
    return apiClient.createDriversCommentary(data);
  },

  getPending: async (): Promise<DriversCommentary[]> => {
    return apiClient.getPendingDriversCommentary();
  },

  commit: async (): Promise<any> => {
    return apiClient.commitDriversCommentaryChanges();
  },

  cancelAll: async (): Promise<any> => {
    return apiClient.cancelDriversCommentaryChanges();
  },

  cancelOne: async (date: string, driverReport: string): Promise<any> => {
    return apiClient.cancelIndividualDriverCommentaryChange(date, driverReport);
  },
};


export const driverPricesAPI = {
  getAll: async (): Promise<any[]> => {
    return apiClient.getDriverPriceChanges();
  },

  getPending: async (): Promise<any[]> => {
    return apiClient.getPendingDriverPriceChanges();
  },

  upsert: async (date: string, driver: string, value: string | null): Promise<any> => {
    return apiClient.upsertDriverPriceChange({ date, driver, value });
  },

  upsertBatch: async (changes: Array<{date: string, driver: string, value: string | null}>): Promise<void> => {
    for (const change of changes) {
      await apiClient.upsertDriverPriceChange(change);
    }
  },

  commit: async (): Promise<any> => {
    return apiClient.commitDriverPriceChanges();
  },

  cancelAll: async (): Promise<any> => {
    return apiClient.cancelDriverPriceChanges();
  },

  cancelOne: async (date: string): Promise<any> => {
    return apiClient.cancelIndividualDriverPriceChange(date);
  },
};

// ==================== PE ACC Commentary API ====================

export const peaccAPI = {
  getAll: async (): Promise<PECommentary[]> => {
    return apiClient.getPECommentary();
  },

  create: async (data: PECommentary, commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<PECommentary> => {
    return commentaryType === 'final'
      ? apiClient.createPECommentaryFinal(data)
      : apiClient.createPECommentary(data);
  },

  getPending: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<PECommentary[]> => {
    return commentaryType === 'final'
      ? apiClient.getPendingPECommentaryFinal()
      : apiClient.getPendingPECommentary();
  },

  commit: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.commitPECommentaryChangesFinal()
      : apiClient.commitPECommentaryChanges();
  },

  cancelAll: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.cancelPECommentaryChangesFinal()
      : apiClient.cancelPECommentaryChanges();
  },

  cancelOne: async (date: string, commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.cancelIndividualPECommentaryChangeFinal(date)
      : apiClient.cancelIndividualPECommentaryChange(date);
  },
};

// ==================== PP ACC Commentary API ====================

export const ppaccAPI = {
  getAll: async (): Promise<PPCommentary[]> => {
    return apiClient.getPPCommentary();
  },

  create: async (data: PPCommentary, commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<PPCommentary> => {
    return commentaryType === 'final'
      ? apiClient.createPPCommentaryFinal(data)
      : apiClient.createPPCommentary(data);
  },

  getPending: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<PPCommentary[]> => {
    return commentaryType === 'final'
      ? apiClient.getPendingPPCommentaryFinal()
      : apiClient.getPendingPPCommentary();
  },

  commit: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.commitPPCommentaryChangesFinal()
      : apiClient.commitPPCommentaryChanges();
  },

  cancelAll: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.cancelPPCommentaryChangesFinal()
      : apiClient.cancelPPCommentaryChanges();
  },

  cancelOne: async (date: string, commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.cancelIndividualPPCommentaryChangeFinal(date)
      : apiClient.cancelIndividualPPCommentaryChange(date);
  },
};

// ==================== PS ACC Commentary API ====================

export const psaccAPI = {
  getAll: async (): Promise<PSCommentary[]> => {
    return apiClient.getPSCommentary();
  },

  create: async (data: PSCommentary, commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<PSCommentary> => {
    return commentaryType === 'final'
      ? apiClient.createPSCommentaryFinal(data)
      : apiClient.createPSCommentary(data);
  },

  getPending: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<PSCommentary[]> => {
    return commentaryType === 'final'
      ? apiClient.getPendingPSCommentaryFinal()
      : apiClient.getPendingPSCommentary();
  },

  commit: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.commitPSCommentaryChangesFinal()
      : apiClient.commitPSCommentaryChanges();
  },

  cancelAll: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.cancelPSCommentaryChangesFinal()
      : apiClient.cancelPSCommentaryChanges();
  },

  cancelOne: async (date: string, commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.cancelIndividualPSCommentaryChangeFinal(date)
      : apiClient.cancelIndividualPSCommentaryChange(date);
  },
};

// ==================== PVC ACC Commentary API ====================

export const pvcaccAPI = {
  getAll: async (): Promise<PVCCommentary[]> => {
    return apiClient.getPVCCommentary();
  },

  create: async (data: PVCCommentary, commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<PVCCommentary> => {
    return commentaryType === 'final'
      ? apiClient.createPVCCommentaryFinal(data)
      : apiClient.createPVCCommentary(data);
  },

  getPending: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<PVCCommentary[]> => {
    return commentaryType === 'final'
      ? apiClient.getPendingPVCCommentaryFinal()
      : apiClient.getPendingPVCCommentary();
  },

  commit: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.commitPVCCommentaryChangesFinal()
      : apiClient.commitPVCCommentaryChanges();
  },

  cancelAll: async (commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.cancelPVCCommentaryChangesFinal()
      : apiClient.cancelPVCCommentaryChanges();
  },

  cancelOne: async (date: string, commentaryType: 'preliminary' | 'final' = 'preliminary'): Promise<any> => {
    return commentaryType === 'final'
      ? apiClient.cancelIndividualPVCCommentaryChangeFinal(date)
      : apiClient.cancelIndividualPVCCommentaryChange(date);
  },
};

// ==================== Forecast Commentary API ====================

export const forecastAPI = {
  getAll: async (): Promise<ForecastCommentary[]> => {
    return apiClient.getForecastCommentary();
  },

  create: async (data: ForecastCommentary): Promise<ForecastCommentary> => {
    return apiClient.createForecastCommentary(data);
  },

  getPending: async (): Promise<ForecastCommentary[]> => {
    return apiClient.getPendingForecastCommentary();
  },

  commit: async (): Promise<any> => {
    return apiClient.commitForecastCommentaryChanges();
  },

  cancelAll: async (): Promise<any> => {
    return apiClient.cancelForecastCommentaryChanges();
  },

  cancelOne: async (forecastQtrDateId: string, forecastQtr: number, polymer: string): Promise<any> => {
    return apiClient.cancelIndividualForecastCommentaryChange(forecastQtrDateId, forecastQtr, polymer);
  },
};

// ==================== Email Bullets API ====================

import type { EmailBullet, CreateBulletPayload, UpdateBulletPayload } from '@/types/emailBullets';

export const bulletsAPI = {
  getAll: async (): Promise<EmailBullet[]> => {
    return apiClient.getEmailBullets();
  },

  getById: async (id: number): Promise<EmailBullet> => {
    return apiClient.getEmailBulletById(id);
  },

  create: async (payload: CreateBulletPayload): Promise<EmailBullet> => {
    return apiClient.createEmailBullet(payload);
  },

  update: async (id: number, payload: UpdateBulletPayload): Promise<EmailBullet> => {
    return apiClient.updateEmailBullet(id, payload);
  },

  delete: async (id: number): Promise<void> => {
    return apiClient.deleteEmailBullet(id);
  },

  // Staging
  staging: {
    getAll: async (): Promise<EmailBullet[]> => apiClient.getStagingBullets(),
    create: async (payload: CreateBulletPayload): Promise<EmailBullet> => apiClient.createStagingBullet(payload),
    update: async (id: number, payload: UpdateBulletPayload): Promise<EmailBullet> => apiClient.updateStagingBullet(id, payload),
    delete: async (id: number): Promise<void> => apiClient.deleteStagingBullet(id),
    publish: async (): Promise<{ published: number }> => apiClient.publishStagingBullets(),
  },
};

// ==================== Daily Spot & Contracts API ====================

export const dailySpotAPI = {
  getAll: async (limit = 90): Promise<any[]> => apiClient.getDailySpotHistory(limit),
  upsert: async (data: any): Promise<any> => apiClient.upsertDailySpotEntry(data),
  getPending: async (): Promise<any[]> => apiClient.getPendingDailySpot(),
  commit: async (): Promise<any> => apiClient.commitDailySpot(),
  cancelAll: async (): Promise<any> => apiClient.cancelAllDailySpot(),
  cancelOne: async (date: string): Promise<any> => apiClient.cancelOneDailySpot(date),
};

export const dailyContractsAPI = {
  upsert: async (data: any): Promise<any> => apiClient.upsertDailyContractsEntry(data),
  getPending: async (): Promise<any[]> => apiClient.getPendingDailyContracts(),
  commit: async (): Promise<any> => apiClient.commitDailyContracts(),
  cancelAll: async (): Promise<any> => apiClient.cancelAllDailyContracts(),
  cancelOne: async (date: string): Promise<any> => apiClient.cancelOneDailyContracts(date),
};

// ==================== Generic Commentary Service ====================

export const commentaryService = {
  drivers: driversAPI,
  driverPrices: driverPricesAPI,
  peacc: peaccAPI,
  ppacc: ppaccAPI,
  psacc: psaccAPI,
  pvcacc: pvcaccAPI,
  forecast: forecastAPI,
  bullets: bulletsAPI,
  dailySpot: dailySpotAPI,
  dailyContracts: dailyContractsAPI,
};

export default commentaryService;
